import React, { useState } from "react";
import Modal from "./Modal";
import {
  clusterApi,
  SerpPreviewResponse,
  ApiError,
} from "../services/api";
import "./SerpPreviewModal.css";

interface SerpPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  keyword: string;
  anchorKeyword: string;
  clientId: string;
  locationCode?: number;
  languageCode?: string;
}

const SerpPreviewModal: React.FC<SerpPreviewModalProps> = ({
  isOpen,
  onClose,
  keyword,
  anchorKeyword,
  clientId,
  locationCode = 2840,
  languageCode = "en",
}) => {
  const [serpData, setSerpData] = useState<SerpPreviewResponse | null>(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setSerpData(null);

    try {
      const response = await clusterApi.getSerpPreview({
        keyword,
        anchor_keyword: anchorKeyword,
        client_id: clientId,
        location_code: locationCode,
        language_code: languageCode,
      });
      setSerpData(response);
      setHasGenerated(true);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`Failed to get SERP data: ${err.message}`);
      } else {
        setError("Failed to get SERP preview. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setSerpData(null);
    setError("");
    setIsLoading(false);
    setHasGenerated(false);
    onClose();
  };

  const handleGenerateAnother = () => {
    setSerpData(null);
    setHasGenerated(false);
    setError("");
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getDomainFromUrl = (url: string) => {
    try {
      return new URL(url).hostname.replace("www.", "");
    } catch {
      return url;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`SERP Preview for "${keyword}"`}
      maxWidth="900px"
    >
      <div className="serp-preview-modal">
        {!hasGenerated ? (
          <form onSubmit={handleSubmit} className="serp-form">
            <div className="form-group">
              <label className="form-label">Live SERP Preview</label>
              <span className="field-hint">
                Fetches the current Google organic search results for this
                keyword in real time.
              </span>
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="form-actions">
              <button
                type="button"
                onClick={handleClose}
                className="btn btn-secondary"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="loading-spinner-button"></span>
                    Generating...
                  </>
                ) : (
                  "Generate SERP Preview"
                )}
              </button>
            </div>
          </form>
        ) : (
          <div className="serp-results">
            {isLoading ? (
              <div className="loading-state">
                <span className="loading-spinner"></span>
                <p>Generating SERP preview...</p>
              </div>
            ) : serpData ? (
              <>
                <div className="serp-timeline">
                  {serpData.snapshots.map((snapshot, index) => (
                    <div key={index} className="serp-snapshot">
                      <div className="snapshot-header">
                        <h4 className="snapshot-date">
                          {formatDate(snapshot.date)}
                        </h4>
                        <div className="snapshot-meta">
                          <span className="total-results">
                            {snapshot.total_results} results
                          </span>
                          {snapshot.your_rank && (
                            <span className="your-rank">
                              Your rank: #{snapshot.your_rank}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="serp-results-list">
                        {snapshot.results.map((result, resultIndex) => (
                          <div key={resultIndex} className="serp-result-item">
                            <div className="result-rank">
                              #{resultIndex + 1}
                            </div>
                            <div className="result-content">
                              <h5 className="result-title">{result.title}</h5>
                              <p className="result-url">
                                <span className="result-domain">
                                  {getDomainFromUrl(result.url)}
                                </span>
                                <a
                                  href={result.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="result-link"
                                  title={result.url}
                                >
                                  View
                                </a>
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="form-actions">
                  <button
                    onClick={handleGenerateAnother}
                    className="btn btn-secondary"
                  >
                    Refresh Results
                  </button>
                  <button onClick={handleClose} className="btn btn-primary">
                    Close
                  </button>
                </div>
              </>
            ) : (
              <div className="empty-state">
                <p>
                  No SERP data was found for this keyword. Please try again.
                </p>
                <div className="form-actions">
                  <button
                    onClick={handleGenerateAnother}
                    className="btn btn-secondary"
                  >
                    Try Again
                  </button>
                  <button onClick={handleClose} className="btn btn-primary">
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default SerpPreviewModal;
